/**
 * CommunicationSettingsAdmin — Config Agent Sirius (LLM) + Google Photos + Meta
 */
import React, { useState, useEffect } from 'react';
import {
  Cloud, CheckCircle, XCircle, Loader, Unlink, ExternalLink,
  Sparkles, Zap, Eye, EyeOff, Save
} from 'lucide-react';
import {
  getGooglePhotosStatus, connectGooglePhotos, disconnectGooglePhotos,
  getCommunicationSettings, updateCommunicationSettings, testCommunicationLLM
} from '../../api';
import { useToast } from '../../contexts/NotificationContext';

const LLM_PROVIDERS = [
  { value: 'claude', label: 'Claude (Anthropic)', models: ['claude-sonnet-4-6', 'claude-haiku-4-5-20251001', 'claude-opus-4-6'] },
  { value: 'openai', label: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'] },
  { value: 'gemini', label: 'Google Gemini', models: ['gemini-2.5-flash', 'gemini-2.5-pro'] },
  { value: 'mistral', label: 'Mistral', models: ['mistral-large-latest', 'mistral-medium-latest'] },
  { value: 'ollama', label: 'Ollama (local)', models: ['qwen2.5:7b', 'llama3.2:3b'] },
  { value: 'cortex', label: 'Cortex (Delogic)', models: ['ollama/qwen2.5:7b', 'ollama/mistral:7b', 'ollama/llama3.2:3b'] },
];

export default function CommunicationSettingsAdmin() {
  const toast = useToast();
  const [googleStatus, setGoogleStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);

  // LLM config
  const [llmForm, setLlmForm] = useState({ llmProvider: '', llmModel: '', llmApiKey: '' });
  const [hasApiKey, setHasApiKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingLlm, setSavingLlm] = useState(false);
  const [testingLlm, setTestingLlm] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [gStatus, settings] = await Promise.all([
        getGooglePhotosStatus().catch(() => ({ data: {} })),
        getCommunicationSettings().catch(() => ({ data: {} })),
      ]);
      setGoogleStatus(gStatus.data);
      setLlmForm({
        llmProvider: settings.data.llmProvider || '',
        llmModel: settings.data.llmModel || '',
        llmApiKey: '',
      });
      setHasApiKey(settings.data.hasLlmApiKey || false);
    } catch (err) {
      toast.error('Erreur chargement paramètres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Google Photos callback detection
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('gphotos_success');
    const error = params.get('gphotos_error');
    if (success) {
      toast.success(`Google Photos connecté pour ${success}`);
      window.history.replaceState({}, '', window.location.pathname);
      load();
    }
    if (error) {
      toast.error(`Erreur Google Photos : ${error}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // --- Google Photos ---
  const handleConnect = async (structureKey) => {
    try {
      setConnecting(structureKey);
      const { data } = await connectGooglePhotos(structureKey);
      window.location.href = data.authUrl;
    } catch (err) {
      toast.error('Erreur connexion Google Photos');
      setConnecting(null);
    }
  };

  const handleDisconnect = async (structureKey) => {
    if (!window.confirm(`Déconnecter Google Photos pour ${structureKey === 'bibou' ? 'Bibou' : 'Choupi'} ?`)) return;
    try {
      await disconnectGooglePhotos(structureKey);
      toast.success('Google Photos déconnecté');
      load();
    } catch (err) {
      toast.error('Erreur déconnexion');
    }
  };

  // --- LLM Sirius ---
  const handleSaveLlm = async () => {
    try {
      setSavingLlm(true);
      await updateCommunicationSettings({
        llmProvider: llmForm.llmProvider || null,
        llmModel: llmForm.llmModel || null,
        ...(llmForm.llmApiKey && { llmApiKey: llmForm.llmApiKey }),
      });
      toast.success('Configuration Sirius sauvegardée');
      if (llmForm.llmApiKey) setHasApiKey(true);
      setLlmForm(f => ({ ...f, llmApiKey: '' }));
      setShowApiKey(false);
    } catch (err) {
      toast.error('Erreur sauvegarde');
    } finally {
      setSavingLlm(false);
    }
  };

  const handleTestLlm = async () => {
    try {
      setTestingLlm(true);
      const { data } = await testCommunicationLLM();
      if (data.success) {
        toast.success(`Sirius connecté ! Réponse : "${data.response}"`);
      } else {
        toast.error(`Test échoué. Réponse : "${data.response}"`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur test connexion');
    } finally {
      setTestingLlm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <Loader className="animate-spin mr-2" size={18} /> Chargement...
      </div>
    );
  }

  const currentProvider = LLM_PROVIDERS.find(p => p.value === llmForm.llmProvider);

  const structures = [
    { key: 'bibou', label: 'Ô P\'tites Quenottes de Bibou', email: 'optitesquenottesdebibou@gmail.com', connected: googleStatus?.bibou },
    { key: 'choupi', label: 'Ô P\'tites Quenottes de Choupi', email: 'optitesquenottesdechoupi@gmail.com', connected: googleStatus?.choupi },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Paramètres Communication</h2>

      {/* Agent Sirius — LLM Config */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={20} className="text-violet-500" />
          <h3 className="font-medium text-gray-900">Agent Sirius — Configuration LLM</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Sirius est l'assistant IA qui rédige les gazettes et les posts réseaux sociaux. Configurez le provider LLM pour activer la co-écriture.
        </p>

        <div className="space-y-4">
          {/* Provider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
            <select
              value={llmForm.llmProvider}
              onChange={e => setLlmForm(f => ({ ...f, llmProvider: e.target.value, llmModel: '' }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Sélectionner un provider...</option>
              {LLM_PROVIDERS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Model */}
          {currentProvider && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
              <select
                value={llmForm.llmModel}
                onChange={e => setLlmForm(f => ({ ...f, llmModel: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Défaut</option>
                {currentProvider.models.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          )}

          {/* API Key */}
          {llmForm.llmProvider && llmForm.llmProvider !== 'ollama' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clé API
                {hasApiKey && !llmForm.llmApiKey && (
                  <span className="ml-2 text-xs text-green-600 font-normal">Configurée</span>
                )}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={llmForm.llmApiKey}
                    onChange={e => setLlmForm(f => ({ ...f, llmApiKey: e.target.value }))}
                    placeholder={hasApiKey ? '••••••••• (laisser vide pour garder l\'existante)' : 'Entrez votre clé API...'}
                    className="w-full border rounded-lg px-3 py-2 text-sm pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                  >
                    {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleSaveLlm}
              disabled={savingLlm || !llmForm.llmProvider}
              className="flex items-center gap-1.5 bg-violet-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-violet-700 disabled:opacity-50"
            >
              {savingLlm ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
              Sauvegarder
            </button>
            {hasApiKey && (
              <button
                onClick={handleTestLlm}
                disabled={testingLlm}
                className="flex items-center gap-1.5 border border-violet-300 text-violet-700 px-4 py-2 rounded-lg text-sm hover:bg-violet-50 disabled:opacity-50"
              >
                {testingLlm ? <Loader size={14} className="animate-spin" /> : <Zap size={14} />}
                Tester la connexion
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Google Photos */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Cloud size={20} className="text-blue-500" />
          <h3 className="font-medium text-gray-900">Google Photos</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Connectez les comptes Google de chaque structure pour importer des photos directement depuis Google Photos dans l'éditeur de gazette.
        </p>

        <div className="space-y-3">
          {structures.map(s => (
            <div key={s.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                {s.connected ? (
                  <CheckCircle size={20} className="text-green-500" />
                ) : (
                  <XCircle size={20} className="text-gray-300" />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-800">{s.label}</p>
                  <p className="text-xs text-gray-400">{s.email}</p>
                </div>
              </div>
              <div>
                {s.connected ? (
                  <button
                    onClick={() => handleDisconnect(s.key)}
                    className="flex items-center gap-1.5 text-red-500 hover:text-red-700 text-sm"
                  >
                    <Unlink size={14} /> Déconnecter
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnect(s.key)}
                    disabled={connecting === s.key}
                    className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {connecting === s.key ? <Loader size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                    Connecter
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Meta API — Sprint 6 */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 opacity-60">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg">📱</span>
          <h3 className="font-medium text-gray-900">Réseaux sociaux (Meta)</h3>
        </div>
        <p className="text-sm text-gray-400">Configuration Facebook + Instagram — à venir</p>
      </div>
    </div>
  );
}
